import { useRef, useState } from "react";
import {
  Autocomplete,
  Button,
  DataGrid,
  Divider,
  Grid,
  ListBoxAutocomplete,
  MaskedInput,
  MenuItem,
  Select,
  TextareaAutosize,
  TextField,
  Tooltip,
  Typography,
} from "../../../../../components";
import "./informacoes.styles.css";
import { responseErros, timeoutBuscaAutocomplete } from "../../../../../utils";
import { PessoaService } from "../../../../../services";
import IntermediadorDialog from "../dialogComponents/intermediador-dialog.component";
import shortid from "shortid";
import { format } from "date-fns";

const TIPO_DOCUMENTO_REFERENCIA = [
  { value: "NOTA_DEVOLUCAO", label: "NOTA DEVOLUÇÃO" },
  { value: "NOTA_DEBITO", label: "NOTA DE DÉBITO (PAG. ANTECIPADO)" },
  { value: "ENTE_GOVERNAMENTAL", label: "ENTE GOVERNAMENTAL" },
  { value: "OUTROS", label: "OUTROS" },
];

const NfSaidaInformacoes = ({
  nfSaidaId,
  nfSaidaEntity,
  setNfSaida,
  intermediadorSelecionado,
  setIntermediadorSelecionado,
  handleInputChange,
  buscarNfSaidaReferencia,
  documentosReferenciados,
  setDocumentosReferenciados,
  nfSaidaListReferencia,
  setNfSaidaListReferencia,
}) => {
  const [inputRef, inputRefNfSaida] = [useRef(), useRef()];
  const [intermediadores, setIntermediadores] = useState([]);
  const [loadingAutoComplete, setLoadingAutoComplete] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [infoDialog, setInfoDialog] = useState({});
  const [editarIntermediador, setEditarIntermediador] = useState(false);
  const [nfSaidaListSelect, setNfSaidaListSelect] = useState([]);
  const [documentoReferencia, setDocumentoReferencia] = useState({});
  const pessoaService = new PessoaService();

  const colunasDocumentosReferenciados = [
    {
      field: "nfSaidaReferenciaId",
      headerName: "NF de Saída",
      flex: 300,
      valueGetter: (params) => {
        const nfSaida = nfSaidaListReferencia.find(
          (nf) => nf.id === params.row.nfSaidaReferenciaId,
        );
        return nfSaida
          ? `N°: ${nfSaida.numeroDocumento} | Série: ${nfSaida.serieDocumento}`
          : "";
      },
      sortable: false,
    },
    {
      field: "chaveDocumentoReferencia",
      headerName: "Documento de Referência",
      flex: 500,
      sortable: false,
    },
    {
      field: "tipoDocumentoReferencia",
      headerName: "Tipo de Documento",
      flex: 200,
      valueGetter: (params) => {
        const tipo = TIPO_DOCUMENTO_REFERENCIA.find(
          (tipo) => tipo.value === params.row.tipoDocumentoReferencia,
        );
        return tipo ? tipo.label : "";
      },
      sortable: false,
    },
  ];

  const buscaPessoas = async function (filter) {
    const filtro = {
      ...filter,
      disabledInclude: true,
      attributes: ["id", "nomeRazaoSocial", "cnpjCpf"],
    };
    const result = await pessoaService.getAll(filtro);
    if (!result.isAxiosError) {
      setIntermediadores(result.data.rows);
      setLoadingAutoComplete(false);
    } else {
      responseErros(result);
    }
  };

  const onInputChangeAutocomplete = (event) => {
    if (event.key === "Enter" && inputRef.current === document.activeElement) {
      const eventValue = event.target?.value;
      if (eventValue?.length >= 2) {
        const filtros = {
          nomeRazaoSocial: eventValue,
          naturezaSocial: "juridica",
          restritiva: true,
        };
        setLoadingAutoComplete(true);
        timeoutBuscaAutocomplete(buscaPessoas(filtros));
      }
    }
  };

  const onInputChangeAutocompleteNfSaida = (event) => {
    if (
      event.key === "Enter" &&
      inputRefNfSaida.current === document.activeElement
    ) {
      const eventValue = event.target?.value;
      const filtros = {
        numeroDocumento: eventValue,
      };
      setLoadingAutoComplete(true);
      buscarNfSaidaReferencia(filtros).then((result) => {
        setNfSaidaListSelect(result || []);
      });
    }
  };

  const onChangeAutocomplete = (value) => {
    let nomeRazaoSocial = null,
      cnpjCpf = null;
    if (value) {
      nomeRazaoSocial = value.nomeRazaoSocial;
      cnpjCpf = value.cnpjCpf;
    }

    nfSaidaEntity.setIdentificarIntermediador({ nomeRazaoSocial, cnpjCpf });
    setNfSaida((prevState) => ({ ...prevState, nfSaidaEntity }));
    setIntermediadorSelecionado(value);
    setInfoDialog({});
    setEditarIntermediador(false);
  };

  const onChangeAutocompleteNfSaida = (value) => {
    setDocumentoReferencia({
      id: shortid.generate(),
      nfSaidaReferenciaId: value?.id,
      chaveDocumentoReferencia: value?.chaveAcesso,
    });
    if (value && !nfSaidaListReferencia.some((nf) => nf.id === value.id)) {
      setNfSaidaListReferencia((prevState) => [...prevState, value]);
    }
    const tipoDocRef = document.getElementById("tipoDocumentoReferencia");
    if (!tipoDocRef) return;
    tipoDocRef.focus();
    tipoDocRef.dispatchEvent(
      new KeyboardEvent("keydown", {
        key: "ArrowDown",
        code: "ArrowDown",
        keyCode: 40,
        which: 40,
        bubbles: true,
      }),
    );
  };

  function handleDocumentoReferenciaChange(event) {
    const { name, value } = event.target;
    setDocumentoReferencia((prevState) => ({ ...prevState, [name]: value }));
  }

  function handleAdicionarDocumentoReferencia() {
    setDocumentosReferenciados((prevState) => [
      ...prevState,
      documentoReferencia,
    ]);
    setDocumentoReferencia({});
  }

  function handleDeleteDocumentoReferencia(id) {
    setDocumentosReferenciados((prevState) =>
      prevState.filter((doc) => doc.id !== id),
    );
  }

  return (
    <>
      <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
        Identificação do Intermediador
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={5.75}>
          <Autocomplete
            id="intermediadorRazaoSocial"
            name="intermediadorRazaoSocial"
            options={intermediadores}
            noOptionsText="Digite e pressione Enter"
            autoHighlight
            loading={loadingAutoComplete}
            loadingText="Carregando"
            getOptionLabel={(option) => (option ? option.nomeRazaoSocial : "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Razão Social"
                variant="outlined"
                onKeyDown={(e) => onInputChangeAutocomplete(e)}
                inputRef={inputRef}
                InputProps={{
                  ...params.InputProps,
                }}
              />
            )}
            onChange={(_, newValue) => onChangeAutocomplete(newValue)}
            value={intermediadorSelecionado}
          />
        </Grid>
        <Grid
          item
          xs={0.5}
          className="d-flex justify-content-center align-items-center"
        >
          <Tooltip
            title={
              editarIntermediador
                ? "Editar Intermediador"
                : "Adicionar Intermediador"
            }
            arrow
            placement="bottom"
          >
            <Button
              id="buttonAdicionarIntermediador"
              variant="contained"
              size="small"
              sx={{
                height: "35px",
                borderRadius: "20%",
                minWidth: "15px",
                boxShadow: "none",
              }}
              onClick={() => setOpenDialog(true)}
            >
              <i
                className={
                  editarIntermediador ? "ph-bold ph-pencil" : "ph-bold ph-plus"
                }
                style={{ color: "#fff", fontSize: 15 }}
              ></i>
            </Button>
          </Tooltip>
        </Grid>
        <Grid item xs={5.75}>
          <MaskedInput
            id="intermediadorCnpj"
            label={"CNPJ"}
            name="intermediadorCnpj"
            variant="outlined"
            margin="none"
            disabled
            mask={"**.***.***/****-99"}
            value={intermediadorSelecionado?.cnpjCpf ?? ""}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>
      <Divider sx={{ mt: 2, border: "1px solid grey", width: "100%" }} />
      <Typography variant="body2" gutterBottom sx={{ mb: 2, mt: 2 }}>
        {" "}
        Informações complementares
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextareaAutosize
            className="text-area w-100 p-3"
            aria-label="minimum height"
            name="informacoesAdicionais"
            id="informacoesAdicionais"
            disabled={nfSaidaId && nfSaidaEntity?.status !== "PENDENTE"}
            onChange={handleInputChange}
            value={nfSaidaEntity?.informacoesAdicionais}
            minRows={5}
            placeholder="Informações adicionais"
          />
        </Grid>
        <Grid item xs={6}>
          <TextareaAutosize
            className="text-area w-100 p-3"
            aria-label="minimum height"
            name="informacoesFisco"
            id="informacoesFisco"
            disabled={nfSaidaId && nfSaidaEntity?.status !== "PENDENTE"}
            onChange={handleInputChange}
            value={nfSaidaEntity?.informacoesFisco}
            minRows={5}
            placeholder="Informações fisco"
          />
        </Grid>
      </Grid>
      <Divider sx={{ mt: 2, border: "1px solid grey", width: "100%" }} />
      <Typography variant="body2" gutterBottom sx={{ mb: 2, mt: 2 }}>
        Referenciar Documentos
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={4.4}>
          <Autocomplete
            ListboxComponent={ListBoxAutocomplete}
            id="nfSaidaReferenciaId"
            name="nfSaidaReferenciaId"
            options={nfSaidaListSelect}
            noOptionsText="Digite o número do documento e pressione Enter"
            autoHighlight
            loading={loadingAutoComplete}
            disabled={nfSaidaEntity.status === "ENVIADA"}
            loadingText="Carregando"
            renderOption={(props, option) => {
              return option ? (
                <li {...props} style={{ background: "transparent" }}>
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <div>
                      <span>
                        Nº Doc:{" "}
                        <span className="fw-bold">
                          {option?.numeroDocumento}
                        </span>{" "}
                        | Série:{" "}
                        <span className="fw-bold">
                          {option?.serieDocumento}
                        </span>
                      </span>
                      <Typography variant="body2" className="fw-bolder">
                        Modelo Documento:{" "}
                        <span className="fw-bold">
                          {option?.modeloDocumento}
                        </span>
                      </Typography>
                    </div>
                    <Typography variant="body2" className="fw-bolder">
                      Data Emissão:{" "}
                      <span className="fw-bold">
                        {format(new Date(option?.dataEmissao), "dd/MM/yyyy")}
                      </span>
                    </Typography>
                  </div>
                </li>
              ) : (
                ""
              );
            }}
            getOptionLabel={(option) => {
              let descricaoNf = `N°: ${option?.numeroDocumento} - Série: ${option?.serieDocumento}`;
              return option ? descricaoNf : "";
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="NF de Saída"
                variant="outlined"
                onKeyDown={(e) => onInputChangeAutocompleteNfSaida(e)}
                inputRef={inputRefNfSaida}
                InputProps={{
                  ...params.InputProps,
                }}
              />
            )}
            onChange={(_, newValue) => onChangeAutocompleteNfSaida(newValue)}
            value={
              nfSaidaListReferencia.find(
                (item) => item.id === documentoReferencia?.nfSaidaReferenciaId,
              ) || null
            }
          />
        </Grid>
        <Grid item xs={4.55}>
          <TextField
            id="chaveDocumentoReferencia"
            name="chaveDocumentoReferencia"
            label="Documento de Referência"
            variant="outlined"
            margin="none"
            disabled
            fullWidth
            onChange={handleDocumentoReferenciaChange}
            value={documentoReferencia?.chaveDocumentoReferencia ?? ""}
          />
        </Grid>
        <Grid item xs={2.5}>
          <Select
            id="tipoDocumentoReferencia"
            name="tipoDocumentoReferencia"
            label="Tipo de Documento"
            variant="outlined"
            margin="none"
            disabled={nfSaidaEntity.status === "ENVIADA"}
            fullWidth
            onChange={handleDocumentoReferenciaChange}
            value={documentoReferencia?.tipoDocumentoReferencia ?? ""}
          >
            {TIPO_DOCUMENTO_REFERENCIA.map((tipo) => (
              <MenuItem key={tipo.value} value={tipo.value}>
                {tipo.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={0.55} sx={{ mt: "0.5em" }}>
          <Button
            id="buttonAdicionar"
            variant="contained"
            color="success"
            size="sall"
            disabled={nfSaidaEntity.status === "ENVIADA"}
            sx={{
              height: "35px",
              borderRadius: "50%",
              minWidth: "15px",
              boxShadow: "none",
            }}
            onClick={() => handleAdicionarDocumentoReferencia()}
          >
            <i
              className="ph-bold ph-plus"
              style={{ color: "#fff", fontSize: 15 }}
            ></i>
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={2} className="mt-2">
        <Grid item xs={12}>
          <DataGrid
            rows={documentosReferenciados}
            columns={colunasDocumentosReferenciados}
            deleteShow={nfSaidaEntity.status !== "ENVIADA"}
            onClickDelete={handleDeleteDocumentoReferencia}
          />
        </Grid>
      </Grid>
      <IntermediadorDialog
        open={openDialog}
        setOpen={setOpenDialog}
        infoDialog={infoDialog}
        setInfoDialog={setInfoDialog}
        setIntermediadorSelecionado={setIntermediadorSelecionado}
        nfSaidaEntity={nfSaidaEntity}
        setNfSaida={setNfSaida}
        setIntermediadores={setIntermediadores}
        setEditarIntermediador={setEditarIntermediador}
      />
    </>
  );
};

export default NfSaidaInformacoes;
